import React, { useState } from 'react'
import { useQuery } from '@apollo/client'
import { get } from 'lodash'
import Error from 'components/error'
import { useAuth } from 'lib/auth'
import {
  flags,
  useFeatureFlags,
  useFeatureFlagsLoading,
} from 'lib/feature-flags'
import { LessonReviewQuery, GetStudentQuery } from 'pages/schedule/queries'
import LessonReviewLayout from './lesson-review-layout'
import LessonReviewRedesign from './redesign/review-booking-redesign'
import ReviewSkeletonRedesign from './redesign/review-skeleton-redesign'
import { ReviewStudentQuery } from './redesign/query'
import ReviewBookingSkeleton from 'components/skeletons/review-booking-skeleton'
import SummaryLayout from './summary-layout'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import { loadGuestBooking } from '../guest-booking'

const ReviewBooking = ({
  idOrSlug,
  lessonTypeId,
  locationId,
  startDateTime,
  studentId,
  previousUrl,
  progressValue,
  stepText,
  guestBooking = false,
}) => {
  const fetchedLessonInfo = useQuery(LessonReviewQuery, {
    variables: {
      id: lessonTypeId,
      includeDeleted: true,
    },
  })
  const [guest] = useState(() => (guestBooking ? loadGuestBooking() : null))
  const [redesignEnabled] = useFeatureFlags([
    flags.FLAG_FEAT_PRIVATE_BOOKING_REDESIGN,
  ])
  // Flags are evaluated anonymously first, then again after sign-in. Wait for
  // the signed-in value so flag-on users do not see the old layout flash.
  const flagsLoading = useFeatureFlagsLoading()
  // The redesign also shows the student's age group and email.
  const fetchedStudent = useQuery(
    redesignEnabled ? ReviewStudentQuery : GetStudentQuery,
    {
      variables: {
        id: studentId,
      },
      skip: guestBooking || !studentId || flagsLoading,
    },
  )

  const { user } = useAuth()
  const coachIsViewingOwnSchedule =
    user?.coach?.coachProfile?.slug === idOrSlug ||
    user?.externalId === idOrSlug

  const [isComplete, setIsComplete] = useState(false)

  if (fetchedLessonInfo.error || fetchedStudent.error) {
    return <Error error={fetchedLessonInfo.error || fetchedStudent.error} />
  }
  if (fetchedLessonInfo.loading || fetchedStudent.loading || flagsLoading) {
    return redesignEnabled ? (
      <ReviewSkeletonRedesign />
    ) : (
      <ReviewBookingSkeleton />
    )
  }
  if (guestBooking && !guest?.contact?.email) {
    return (
      <Error
        error="Student details are missing"
        message="Go back and enter who is attending this session."
      />
    )
  }
  const selectedStudent = guestBooking
    ? {
      firstName: guest.student.firstName,
      lastName: guest.student.lastName,
      isMinor: guest.student.isMinor,
    }
    : fetchedStudent.data?.student

  if (!user && !guestBooking) {
    return <Error error="Not logged in" message="Not logged in" />
  }

  const lessonType = get(fetchedLessonInfo.data, 'lessonTypes.0')
  // useQuery ignores GraphQL errors, so missing lesson types arrive as empty data.
  if (!lessonType || !selectedStudent) {
    return <Error statusCode={404} />
  }

  const timezone = get(lessonType?.facilities, ['0', 'timezone'])
  const Layout = redesignEnabled ? LessonReviewRedesign : LessonReviewLayout

  const eventObject = {
    event: '',
    title: lessonType?.title,
    category: lessonType?.category,
    facility: get(lessonType?.facilities, ['0', 'name']),
    location: `${get(lessonType?.facilities, ['0', 'city'])}, ${get(
      lessonType?.facilities,
      ['0', 'state'],
    )}`,
    price: `${lessonType?.priceInCents / 100}`,
    event_date: DateTime.fromISO(startDateTime, {
      zone: timezone,
    }).toFormat('yyyy-LL-dd'),
    event_time: DateTime.fromISO(startDateTime, {
      zone: timezone,
    }).toFormat('hh:mm a'),
    duration: lessonType?.durationInMinutes,
    coach_id: lessonType?.coach?.id,
    coach_name: lessonType?.coach?.name,
    access_code_required: lessonType?.requiresAccessCode ? 'yes' : 'no',
    has_plus_subscription: lessonType?.coach?.hasPaidTierSubscription,
  }

  return (
    <>
      {!isComplete ? (
        <Layout
          lessonType={lessonType}
          student={selectedStudent}
          locationId={locationId}
          startDateTime={startDateTime}
          previousUrl={previousUrl}
          isComplete={isComplete}
          setIsComplete={setIsComplete}
          coachIsViewingOwnSchedule={coachIsViewingOwnSchedule}
          progressValue={progressValue}
          stepText={stepText}
          eventObject={eventObject}
          guest={guest}
        />
      ) : (
        <SummaryLayout
          lessonType={lessonType}
          student={selectedStudent}
          idOrSlug={idOrSlug}
          lessonTypeId={lessonTypeId}
          locationId={locationId}
          startDateTime={startDateTime}
          previousUrl={previousUrl}
          isComplete={isComplete}
          coachIsViewingOwnSchedule={coachIsViewingOwnSchedule}
          guest={guest}
        />
      )}
    </>
  )
}

ReviewBooking.propTypes = {
  idOrSlug: PropTypes.string.isRequired,
  lessonTypeId: PropTypes.string.isRequired,
  locationId: PropTypes.string.isRequired,
  startDateTime: PropTypes.string.isRequired,
  studentId: PropTypes.string,
  guestBooking: PropTypes.bool,
  previousUrl: PropTypes.string,
  progressValue: PropTypes.number.isRequired,
  stepText: PropTypes.string.isRequired,
}

export default ReviewBooking
